/**
 * Compose an OpenSearch `_search` body from URL params.
 *
 * The structure is always:
 *
 *   bool.filter = [ @timestamp range, ...positive ft clauses ]
 *   bool.must_not = [ ...negative ft clauses ]
 *   bool.must = [ user RQB query ]  // only when rules.length > 0
 *
 * The user's outer AND/OR controls only the contents of `must[0]`, never
 * the position of the timeframe. This fixes the v1 OR + timeframe defect.
 */

import { formatQuery } from 'react-querybuilder'

import { autoDateHistogramAgg } from '../builders/aggs'
import { createBoolQuery } from '../builders/bool'
import { buildHighlight } from '../builders/highlight'
import { timestampRange } from '../builders/range'
import { sortBy } from '../builders/sort'
import { ruleProcessor } from '../processors/rule-processor'
import { filtersToBuckets } from '../utils/filter-to-clause'

import type {
	BoolQuery,
	OpenSearchSearchBody,
	QueryClause,
} from '../types/query'
import type { StructuredFilter } from '../utils/filter-to-clause'
import type { ResolvedTimeRange } from '../utils/time-preset'
import type { RuleGroupType } from 'react-querybuilder'

export type ComposeSearchBodyInput = {
	q?: RuleGroupType
	tr?: ResolvedTimeRange
	ft?: ReadonlyArray<StructuredFilter>
	page?: number
	pageSize?: number
	includeHighlight?: boolean
}

const HIGHLIGHT_FIELDS = ['event.*', 'normalized_fields.*'] as const
const DEFAULT_PAGE_SIZE = 100

function hasRules(q: RuleGroupType | undefined): q is RuleGroupType {
	return !!q && Array.isArray(q.rules) && q.rules.length > 0
}

function buildUserQueryClause(q: RuleGroupType): QueryClause | undefined {
	const formatted = formatQuery(q, {
		format: 'elasticsearch',
		ruleProcessor,
	}) as QueryClause | Record<string, never> | undefined

	if (!formatted || Object.keys(formatted).length === 0) return undefined
	return formatted as QueryClause
}

export function composeSearchBody({
	q,
	tr,
	ft,
	page = 1,
	pageSize = DEFAULT_PAGE_SIZE,
	includeHighlight = true,
}: ComposeSearchBodyInput): OpenSearchSearchBody {
	const buckets = filtersToBuckets(ft ?? [])

	const filterClauses: Array<QueryClause> = []
	if (tr) filterClauses.push(timestampRange(tr.st, tr.ed))
	filterClauses.push(...buckets.filter)

	const mustClauses: Array<QueryClause> = []
	if (hasRules(q)) {
		const userClause = buildUserQueryClause(q)
		if (userClause) mustClauses.push(userClause)
	}

	const bool: BoolQuery = createBoolQuery({
		filter: filterClauses,
		must_not: buckets.mustNot,
		must: mustClauses,
	})

	const body: OpenSearchSearchBody = {
		size: pageSize,
		from: (page - 1) * pageSize,
		query: bool,
		sort: [sortBy('@timestamp', 'desc')],
		docvalue_fields: [{ field: '@timestamp', format: 'date_time' }],
		aggs: {
			events_over_time: autoDateHistogramAgg('@timestamp'),
		},
		track_total_hits: true,
	}

	if (includeHighlight && hasRules(q)) {
		body.highlight = buildHighlight(HIGHLIGHT_FIELDS)
	}

	return body
}

export type ComposeCommonValuesInput = {
	field: string
	tr?: ResolvedTimeRange
	ft?: ReadonlyArray<StructuredFilter>
	q?: RuleGroupType
	size?: number
}

/**
 * Body for the common-values popover request. Same filters/timeframe/q as
 * the main search, but `size: 0` and a terms aggregation on the requested
 * field's `.keyword` companion.
 */
export function composeCommonValuesBody({
	field,
	tr,
	ft,
	q,
	size = 10,
}: ComposeCommonValuesInput): OpenSearchSearchBody {
	const buckets = filtersToBuckets(ft ?? [])

	const filterClauses: Array<QueryClause> = []
	if (tr) filterClauses.push(timestampRange(tr.st, tr.ed))
	filterClauses.push(...buckets.filter)
	filterClauses.push({ exists: { field } })

	const mustClauses: Array<QueryClause> = []
	if (hasRules(q)) {
		const userClause = buildUserQueryClause(q)
		if (userClause) mustClauses.push(userClause)
	}

	const bool: BoolQuery = createBoolQuery({
		filter: filterClauses,
		must_not: buckets.mustNot,
		must: mustClauses,
	})

	const keywordField = field.endsWith('.keyword') ? field : `${field}.keyword`

	return {
		size: 0,
		query: bool,
		aggs: {
			display_names: {
				terms: { field: keywordField, size },
			},
		},
	}
}
